import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useBlog } from '../context/BlogContext'
import { urlFor } from '../lib/sanityClient'

const CATEGORY_LABELS = {
  'industry-news': 'Industry News',
  'product-updates': 'Product Updates',
  'guides-tips': 'Guides & Tips',
  'case-studies': 'Case Studies',
  'company-news': 'Company News',
  technology: 'Technology',
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function BlogCard({ post, index, featured = false }) {
  const imageUrl = post.coverImage
    ? urlFor(post.coverImage).width(featured ? 900 : 600).height(featured ? 500 : 340).auto('format').fit('crop').url()
    : null

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`group card overflow-hidden cursor-pointer flex flex-col ${featured ? 'md:flex-row' : ''}`}
    >
      <Link to={`/blog/${post.slug}`} className="contents">
        {/* Cover image */}
        <div
          className={`relative overflow-hidden bg-surface-2 flex-shrink-0 ${
            featured ? 'md:w-1/2 h-56 md:h-auto' : 'h-48'
          }`}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={post.coverImage?.alt || post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/10 to-accent/5">
              <svg
                className="w-12 h-12 text-accent/30"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                />
              </svg>
            </div>
          )}

          {/* Category badge */}
          {post.category && (
            <span className="absolute top-3 left-3 badge bg-white/90 dark:bg-slate-900/90 text-accent backdrop-blur-sm text-xs">
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
          )}
        </div>

        {/* Content */}
        <div className={`flex flex-col flex-1 p-6 ${featured ? 'md:p-8' : ''}`}>
          {/* Meta */}
          <div className="flex items-center gap-3 text-xs text-secondary mb-3">
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            {post.estimatedReadingTime && (
              <>
                <span className="w-1 h-1 rounded-full bg-border-strong" />
                <span>{post.estimatedReadingTime} min read</span>
              </>
            )}
          </div>

          {/* Title */}
          <h3
            className={`font-semibold text-primary leading-snug mb-3 group-hover:text-accent transition-colors duration-200 ${
              featured ? 'text-xl md:text-2xl' : 'text-base md:text-lg'
            }`}
          >
            {post.title}
          </h3>

          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-secondary text-sm leading-relaxed line-clamp-3 flex-1">
              {post.excerpt}
            </p>
          )}

          {/* Author + CTA */}
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border-subtle">
            {post.author ? (
              <div className="flex items-center gap-2">
                {post.authorImage ? (
                  <img
                    src={urlFor(post.authorImage).width(32).height(32).fit('crop').url()}
                    alt={post.author}
                    className="w-7 h-7 rounded-full object-cover border border-border-subtle"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent text-xs font-semibold">
                      {post.author.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-xs font-medium text-secondary">{post.author}</span>
              </div>
            ) : (
              <span />
            )}

            <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent group-hover:gap-2 transition-all duration-200">
              Read more
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  )
}

export default function BlogSection() {
  const { posts, loading, hasActivePosts } = useBlog()

  if (loading || !hasActivePosts) return null

  const [featured, ...rest] = posts
  const preview = rest.slice(0, 2)

  return (
    <section className="section-padding bg-surface-1 dark:bg-surface-0">
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12"
        >
          <div>
            <span className="section-label">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              From the Blog
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-1">
              Latest Insights
            </h2>
            <p className="text-secondary mt-2 max-w-xl">
              Thoughts, guides and updates from the Clutch team.
            </p>
          </div>
          <Link
            to="/blog"
            className="btn-ghost text-sm py-2.5 px-5 flex-shrink-0"
          >
            View all posts
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
            </svg>
          </Link>
        </motion.div>

        {/* Grid */}
        <div className="space-y-6">
          {/* Featured post */}
          <BlogCard post={featured} index={0} featured />

          {/* Secondary posts */}
          {preview.length > 0 && (
            <div className={`grid gap-6 ${preview.length === 1 ? 'md:grid-cols-1 max-w-lg' : 'md:grid-cols-2'}`}>
              {preview.map((post, i) => (
                <BlogCard key={post._id} post={post} index={i + 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
